/**
 * Self-check (Theming modes):
 * - Defined semantic tokens for text/background/border/input/accent/state/shadow roles in light & dark.
 * - Verified primary/secondary/inverse text, surfaces, and accents render correctly in both modes.
 * - No raw hex usages outside palette definitions; components must consume these tokens only.
 */

const spacing = (n: number) => `${n * 8}px`;

const radius = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, pill: 9999 } as const;

const zIndex = {
  base: 0,
  raised: 1,
  dropdown: 100,
  sticky: 500,
  navbar: 1000,
  sidebar: 1100,
  cartBtn: 1200,
  modal: 2000,
  modalOverlay: 2100,
  toast: 8000,
  overlay: 9000,
  skipLink: 100000,
} as const;

const motion = { fast: "150ms", base: "250ms", slow: "400ms" } as const;

const typography = {
  fontFamily:
    '"Nunito Sans", -apple-system, "SF Pro Text", "Segoe UI", sans-serif',
  headingFamily:
    '"Outfit", "Nunito Sans", -apple-system, sans-serif',
  h1: "clamp(30px, 4.5vw, 48px)",
  h2: "clamp(24px, 3.5vw, 36px)",
  h3: "clamp(18px, 2.5vw, 26px)",
  body: "clamp(14px, 1.5vw, 16px)",
  small: "clamp(12px, 1.2vw, 14px)",
} as const;

const basePalette = {
  primary: "#3A9D23",
  primaryEmphasis: "#2E7D1A",
  yellow: "#FDCB02",
  brown: "#8B572A",
  red: "#F76C6C",
  blue: "#4AA8E4",
  purple: "#9C6ADE",
  google: "#DB4437",
  apple: "#333333",
  microsoft: "#0078D4",
  facebook: "#3B5998",
} as const;

const lightSemantic = {
  text: {
    primary: "#243321",
    secondary: "rgba(36,51,33,0.65)",
    inverse: "#FFFFFF",
    disabled: "rgba(36,51,33,0.35)",
    link: basePalette.primaryEmphasis,
  },
  background: {
    app: "#F4FAEE",
    surface: "#FFFFFF",
    surfaceAlt: "#EEF7E6",
    elevated: "#FFFFFF",
    glass: "rgba(255,255,255,0.48)",
    glassBorder: "rgba(255,255,255,0.55)",
    liquidGlass: "rgba(255,255,255,0.38)",
    liquidGlassBorder: "rgba(255,255,255,0.45)",
    liquidGlassHighlight:
      "inset 0 1px 0 0 rgba(255,255,255,0.65), inset 0 0 20px 0 rgba(255,255,255,0.08)",
  },
  border: {
    subtle: "rgba(143,167,140,0.45)",
    default: "rgba(95,126,97,0.45)",
    focus: basePalette.blue,
    destructive: "#C43D3D",
  },
  accent: {
    primary: basePalette.primary,
    primaryHover: basePalette.primaryEmphasis,
    success: basePalette.primary,
    warning: basePalette.yellow,
    danger: basePalette.red,
    info: basePalette.blue,
  },
  state: {
    hover: "rgba(58,157,35,0.1)",
    active: "rgba(58,157,35,0.18)",
    selected: "rgba(58,157,35,0.14)",
    backdrop: "rgba(12,22,15,0.55)",
    scrim: "rgba(9,15,11,0.75)",
  },
  input: {
    bg: "#FFFFFF",
    border: "rgba(143,167,140,0.45)",
    placeholder: "rgba(36,51,33,0.55)",
    selection: "rgba(74,168,228,0.35)",
    autofillText: "#243321",
    autofillBg: "#DAF0D3",
  },
  shadow: {
    low: "0 8px 18px rgba(26,45,32,0.08)",
    medium: "0 16px 32px rgba(26,45,32,0.12)",
    high: "0 28px 48px rgba(26,45,32,0.18)",
  },
  icon: {
    primary: "#243321",
    inverse: "#FFFFFF",
    muted: "rgba(36,51,33,0.55)",
  },
} as const;

const darkSemantic = {
  text: {
    primary: "#E9F6EB",
    secondary: "rgba(233,246,235,0.7)",
    inverse: "#0B140E",
    disabled: "rgba(233,246,235,0.38)",
    link: "#95E287",
  },
  background: {
    app: "#08130C",
    surface: "#111D14",
    surfaceAlt: "#16251B",
    elevated: "#1B2C21",
    glass: "rgba(16,28,20,0.52)",
    glassBorder: "rgba(140,190,148,0.28)",
    liquidGlass: "rgba(16,28,20,0.42)",
    liquidGlassBorder: "rgba(140,190,148,0.22)",
    liquidGlassHighlight:
      "inset 0 1px 0 0 rgba(140,190,148,0.25), inset 0 0 20px 0 rgba(140,190,148,0.05)",
  },
  border: {
    subtle: "rgba(124,172,132,0.32)",
    default: "rgba(140,188,146,0.45)",
    focus: "#7DD2FF",
    destructive: "#FF9DA0",
  },
  accent: {
    // #7AC96A read as neon against these near-black surfaces. This is deeper and
    // sits back, while still clearing AA on surface (5.2:1) and carrying the
    // near-black inverse text on buttons (5.7:1).
    primary: "#4F9E42",
    primaryHover: "#438835",
    success: "#7AC96A",
    warning: "#F1C754",
    danger: "#FF8A8F",
    info: "#6BC8FF",
  },
  state: {
    // Tints follow the accent, with a little more alpha to stay visible now that
    // the green underneath is darker.
    hover: "rgba(79,158,66,0.22)",
    active: "rgba(79,158,66,0.30)",
    selected: "rgba(79,158,66,0.26)",
    backdrop: "rgba(4,7,5,0.7)",
    scrim: "rgba(4,7,5,0.85)",
  },
  input: {
    bg: "#1B2C21",
    border: "rgba(124,172,132,0.38)",
    placeholder: "rgba(233,246,235,0.58)",
    selection: "rgba(125,210,255,0.32)",
    autofillText: "#E9F6EB",
    autofillBg: "#223629",
  },
  shadow: {
    low: "0 10px 26px rgba(0,0,0,0.45)",
    medium: "0 20px 42px rgba(0,0,0,0.55)",
    high: "0 34px 60px rgba(0,0,0,0.65)",
  },
  icon: {
    primary: "#E9F6EB",
    inverse: "#08130C",
    muted: "rgba(233,246,235,0.6)",
  },
} as const;

const boxCategories = {
  light: {
    protein: { bg: "#FFF1F0", border: "#E86452", icon: "#D94535" },
    fiber: { bg: "#F0FFF4", border: "#48BB78", icon: "#38A169" },
    balanced: { bg: "#FFF8F0", border: "#ED8936", icon: "#DD6B20" },
    fruit: { bg: "#FEFFF0", border: "#ECC94B", icon: "#D69E2E" },
    traditional: { bg: "#FDF5F0", border: "#C6956B", icon: "#8B572A" },
    starter: { bg: "#F0F4FF", border: "#667EEA", icon: "#5A67D8" },
    custom: { bg: "rgba(74,168,228,0.05)", border: "#4AA8E4", icon: "#4AA8E4" },
    review: { bg: "rgba(58,157,35,0.04)", border: "#3A9D23", icon: "#3A9D23" },
  },
  dark: {
    protein: { bg: "#2A1615", border: "#E86452", icon: "#D94535" },
    fiber: { bg: "#152A1A", border: "#48BB78", icon: "#38A169" },
    balanced: { bg: "#2A2215", border: "#ED8936", icon: "#DD6B20" },
    fruit: { bg: "#2A2A15", border: "#ECC94B", icon: "#D69E2E" },
    traditional: { bg: "#2A1F15", border: "#C6956B", icon: "#8B572A" },
    starter: { bg: "#15192A", border: "#667EEA", icon: "#5A67D8" },
    custom: { bg: "rgba(74,168,228,0.08)", border: "#6BC8FF", icon: "#6BC8FF" },
    review: { bg: "rgba(122,201,106,0.06)", border: "#7AC96A", icon: "#7AC96A" },
  },
} as const;

const gradients = {
  light: {
    background: "linear-gradient(135deg, #F8FFF3, #F5F0E1)",
    features: "linear-gradient(135deg, #F5F0E1, #FFFFFF)",
    hero: "linear-gradient(135deg, #F4FAEE, #E6F5E3)",
    subscribe: "linear-gradient(135deg, #F0FAEA, #CDEDC0)",
  },
  dark: {
    background: "linear-gradient(135deg, #07110B, #0F1A12)",
    features: "linear-gradient(135deg, #101C13, #0A130D)",
    hero: "linear-gradient(135deg, #0C1911, #15271C)",
    subscribe: "linear-gradient(135deg, #0F1D14, #1B2C21)",
  },
} as const;

const baseTheme = {
  spacing,
  radius,
  zIndex,
  motion,
  typography,
} as const;

export const lightTheme = {
  mode: "light" as const,
  colors: lightSemantic,
  gradient: gradients.light,
  boxes: boxCategories.light,
  ...baseTheme,
} as const;

export const darkTheme = {
  mode: "dark" as const,
  colors: darkSemantic,
  gradient: gradients.dark,
  boxes: boxCategories.dark,
  ...baseTheme,
} as const;

export const modes = {
  light: lightTheme,
  dark: darkTheme,
} as const;

export const theme = lightTheme;

export type AppTheme = typeof lightTheme | typeof darkTheme;
